<template>
  <div ref="fastbooking" class="reb-fastbooking">
    <header class="reb-fastbooking__header">
      <h1 class="reb-fastbooking__hotel" data-test="hotel-header__hotel-title">
        {{ props.hotel.title }}
      </h1>
      <div class="reb-fastbooking__stars" v-html="props.hotel.stars" />
    </header>
    <div class="reb-fastbooking__country">
      {{ props.hotel.country }}
    </div>
    <div class="reb-fastbooking__flight t-link" @click="openFlightTab()">
      {{ i18n.add_flight }}
    </div>
    <FastbookingForm
      class="reb-fastbooking__form"
      @open-fastbooking="openFastbooking()"
      @submit-fastbooking="submitFastbooking()"
    />
  </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue'

import { FASTBOOKING_EVENTS } from '../../../../shared/fastbooking/constants'
import fb from '../../../../shared/fastbooking/ibh_fastbooking'
import { useFastbookingStore } from '../../store/fastbooking'
import FastbookingForm from '../FastbookingForm/FastbookingForm.vue'

const FASTBOOKING_STATE_KEY = 'fastbooking_state'

const i18n = inject('i18n')

/**
 * Props
 */
const props = defineProps({
  hotel: {
    type: Object,
    required: true,
  },
})

/**
 * Refs
 */
const fastbooking = ref(null)

/**
 * Store
 */
const fbStore = useFastbookingStore()

/**
 * Update fastbooking state from local storage
 */
const updateFastbookingState = () => {
  const fastbookingState = JSON.parse(localStorage.getItem(FASTBOOKING_STATE_KEY))

  fbStore.updateState(fastbookingState)
}

/**
 * Init local storage listener for fastbooking state
 */
const initStorageListener = () => {
  document.addEventListener(FASTBOOKING_EVENTS.STATE_UPDATED, updateFastbookingState)
}

const clickFastbookingButton = () => {
  document.querySelector('.js-header-sticky-hotel .btn-floating-fastbooking').click()
}

/**
 * Open original fastbooking
 *
 * Current implementation can only open fastbooking with dates opened
 */
const openFastbooking = () => {
  const scrollY = window.scrollY || window.pageYOffset

  if (scrollY) {
    window.scroll({ top: 0, behavior: 'smooth' })
    setTimeout(() => {
      clickFastbookingButton()
    }, 200)
  } else {
    clickFastbookingButton()
  }
}

/**
 * Open flight tab in original fastbooking
 */
const openFlightTab = () => {
  openFastbooking()
  fb.changeFastbookingTab('flight-plus-hotel')
}

/**
 * This method is intended to be the submit of the fastbooking
 * For now, due to limitations of the legacy fastbooking code, it only opens the fastbooking
 */
const submitFastbooking = () => {
  openFastbooking()
}

onMounted(() => {
  updateFastbookingState()

  initStorageListener()
})
</script>
<style scoped lang="scss">
@use '../../../../base_css/abstracts/abstracts' as *;

.reb-fastbooking {
  @include link-styles;
  display: grid;
  grid-gap: 8px;
  grid-template:
    'header header'
    'country flight'
    'form form' / 1fr 1fr;
  width: 512px;
  padding: 24px;
  border-radius: 4px;
  background-color: var(--c_white);
  color: var(--c_black_grey_700);

  &__header {
    display: grid;
    grid-area: header;
    grid-gap: 0 4px;
    grid-template-rows: 15px 20px;
    grid-template-columns: auto 1fr;
  }

  &__hotel {
    @include fontSize(18);
    grid-row: 1 / 3;
    grid-column: 1 / 2;
    align-self: end;
    font-family: var(--title-font);
  }

  &__stars {
    grid-row: 1 / 2;
    grid-column: 2 / 3;
    align-self: start;
  }

  &__country {
    @include fontSize(14);
    grid-area: country;
  }

  &__flight {
    @include fontSize(14);
    cursor: pointer;
    grid-area: flight;
    justify-self: end;
  }

  &__form {
    grid-area: form;
  }
}
</style>
<style lang="scss">
.fb {
  .rating-stars {
    &::before {
      color: inherit;
    }
  }
}
</style>
